import axios from 'axios';
import dayjs from 'dayjs';
import { FC, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ParentParams, Setting } from '../types/constant/PagesPropsType';
import { Subscription } from '../types/constant/SubscriptionType';
import '../assets/css/style.css';
import chevron from '../assets/images/chevron_right_green.svg';
import iconLoading from '../assets/images/icon_loading_circle.gif';
import subscriptionListClass from '../assets/scss/SubscriptionList/SubscriptionList.module.scss';
import { CUSTOMER_API_URL } from '../constants';
import { ReactiveSubscription } from '../components/SubscriptionList/ReactiveSubscription';
import { addRequiredInfo, editSubscriptions } from '../utils/subscriptionList';
import { postMessageGtag } from '../utils/trackClick';
import { PayBackPointsRate } from '../components/SubscriptionList/PayBackPointsRate';
import {
  PayBackPointsRateModal,
  PayBackPointsRateModalViewType,
} from '../components/PayBackPointsRateModal/PayBackPointsRateModal';
import { ThreeDSecurePrompt } from '../components/SubscriptionList/ThreeDSecurePrompt';
import { useSubscriptionListSubscribedDisplay } from '../hooks/use-subscription-list-subscribed-display';
import { SubscriptionListProductThumbnails } from '../components/SubscriptionList/SubscriptionListProductThumbnails';

export const SubscriptionList: FC<{ setting: Setting; parentParams: ParentParams }> = ({
  setting,
  parentParams,
}) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loadingEndFlg, setLoadingEndFlg] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const [productDisplayUnits, setProductDisplayUnits] = useState<Record<string, string>>({});
  const [isLoadingDisplayUnits, setIsLoadingDisplayUnits] = useState(false);
  const [pointsRateModal, setPointsRateModal] = useState<PayBackPointsRateModalViewType | null>(
    null
  );

  const { subscribedByContractId, layoutReady } = useSubscriptionListSubscribedDisplay(
    subscriptions,
    parentParams,
    setting.token
  );

  useEffect(() => {
    axios
      .post(
        `${CUSTOMER_API_URL}?action=getSubscriptions`,
        {},
        { headers: { Authorization: setting.token } }
      )
      .then((response) => {
        editSubscriptions(response.data).then(async (result) => {
          setSubscriptions(result);

          // すべての契約から一意な商品IDのリストを抽出
          const allProductLines = result.flatMap((sub) => sub.subscriptionLines);

          // ローディング表示後にdisplayUnitを取得
          setLoadingEndFlg(true);

          // バックグラウンドでdisplayUnitを取得
          setIsLoadingDisplayUnits(true);
          try {
            const enhancedLines = await addRequiredInfo(allProductLines);
            // 商品IDとdisplayUnitのマッピングを作成
            const displayUnitMap = enhancedLines.reduce(
              (map, line) => {
                if (line.productId && line.displayUnit) {
                  map[line.productId] = line.displayUnit;
                }
                return map;
              },
              {} as Record<string, string>
            );

            setProductDisplayUnits(displayUnitMap);
          } catch (error) {
            console.error('Error fetching display units:', error);
          } finally {
            setIsLoadingDisplayUnits(false);
          }
        });
      })
      .catch(() => {
        setLoadingEndFlg(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const height = document.querySelector('body')!.scrollHeight;

    setting.resize(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions]);

  // iframeの高さ調整
  useEffect(() => {
    if (loadingEndFlg && root.current) {
      const height = root.current.clientHeight;
      setting.resize(height); // ロード完了後、高さを調整
    }
  }, [loadingEndFlg, setting]);

  let subScriptionNp: any = null;
  if (parentParams?.npFlg == 'true') {
    subScriptionNp = (
      <section>
        <div className="npAtobarai">
          <header>
            <h2 className="account__nav__title account_page_title huckleberry-subscription-list-title huckleberry-subscription-list-color">
              <span>
                NP後払いでご利用中の
                <br className="spVisible" />
                定期便の確認・変更
              </span>
            </h2>
          </header>
          <div className="npAtobaraiContent">
            <p className="f-14 sp-f-12 mb-20">
              定期会員専用マイアカウントページに遷移し、ご利用中の定期便の内容の確認・変更ができます。
              <br />
              （解約・スキップをご希望の場合は、ZENBお客様センターにご連絡ください。）
            </p>

            <span className="btn detail-btn">
              <a
                href="https://t2.zenb.jp/mypage/"
                target="_parent"
              >
                定期便の内容を確認・変更する
              </a>
            </span>
          </div>
        </div>
      </section>
    );
  }

  useEffect(() => {
    window.parent.postMessage({ action: 'subscriptionList' }, '*');
  }, []);

  useEffect(() => {
    const activeSubscriptions = subscriptions.filter(
      (subscription) => subscription.status === 'ACTIVE'
    );
    // ACTIVEが存在するときのみテーマ側へ直近の定期契約IDを送る
    if (activeSubscriptions.length !== 0) {
      // 次回お届け日で昇順
      const ascSortNextDeliverySubscriptions = [...activeSubscriptions].sort((a, b) => {
        const aNextDeliveryDate = dayjs(a.nextDeliveryDate).toDate();
        const bNextDeliveryDate = dayjs(b.nextDeliveryDate).toDate();

        return aNextDeliveryDate.getTime() >= bNextDeliveryDate.getTime() ? 1 : -1;
      });

      const arraySubscriptionContractId =
        ascSortNextDeliverySubscriptions[0].subscriptionContractId.split('/');
      const nextSubscriptionId =
        arraySubscriptionContractId[arraySubscriptionContractId.length - 1];

      window.parent.postMessage(
        {
          action: 'setSubscriptionData',
          data: {
            subscriptionId: nextSubscriptionId,
            isLoading: !loadingEndFlg,
          },
        },
        '*'
      );
    }
  }, [subscriptions, loadingEndFlg]);

  let listContents: JSX.Element[] | JSX.Element = <></>;
  // 定期登録内容の取得が終わるまでローディング表示
  if (!loadingEndFlg) {
    listContents = (
      <div className="subscription-history subscription-history__no-subscription">
        <div className={subscriptionListClass.loading}>
          <img
            src={iconLoading}
            alt=""
            width={64}
            height={64}
          />
          <div>読み込み中です。しばらくお待ちください。</div>
        </div>
      </div>
    );
    // 定期登録内容の取得が終わったらコンテンツ表示
  } else {
    // 定期購買一覧情報の中から、ACTIVEとPAUSEDの契約を取得
    const activeSubscriptions = subscriptions.filter(
      (subscription) => subscription.status === 'ACTIVE'
    );
    const pausedSubscriptions = subscriptions.filter(
      (subscription) => subscription.status === 'PAUSED'
    );

    // 定期購買一覧情報の中に、ACTIVEの契約とPAUSEDの契約がどちらもないとき
    if (activeSubscriptions.length === 0 && pausedSubscriptions.length === 0) {
      listContents = (
        <div className="subscription-history subscription-history__no-subscription">
          <p className="subscription-history__message">いちばんオトクな定期便はじめませんか？</p>
          <a
            href="https://zenb.jp/collections/subscriptioncourses"
            target="_parent"
            onClick={() => {
              postMessageGtag('click_start', 'top');
            }}
          >
            <span className="btn detail-btn">
              定期オトク便をはじめる
              <span className="icon">
                <img
                  src={chevron}
                  alt=""
                  width={14}
                  height={14}
                />
              </span>
            </span>
          </a>
        </div>
      );
      // ACTIVEかPAUSEDの契約があるとき
    } else {
      // ACTIVEの定期情報が上位に来るように結合
      const sortedSubscriptions = [...activeSubscriptions, ...pausedSubscriptions];

      listContents = sortedSubscriptions.map((subscription, index) => {
        // 商品数 4つ以上の場合は、左寄せにする
        const rowSubs = subscribedByContractId[subscription.subscriptionContractId];
        const thumbItems =
          layoutReady && rowSubs && rowSubs.length > 0 ? rowSubs : null;
        const thumbCount =
          thumbItems !== null ? thumbItems.length : subscription.subscriptionLines.length;
        const productCountClass = thumbCount >= 4 ? 'start-left' : '';

        let subscriptionContents: JSX.Element = <></>;

        // 3Dセキュアの認証表示チェック
        const latestBillingAttempt = subscription.billingAttempts[0];
        const nextActionUrl =
          latestBillingAttempt && !latestBillingAttempt.ready && latestBillingAttempt.nextActionUrl
            ? latestBillingAttempt.nextActionUrl
            : '';

        // ACTIVEの契約
        if (subscription.status === 'ACTIVE') {
          subscriptionContents = (
            <>
              {nextActionUrl && <ThreeDSecurePrompt nextActionUrl={nextActionUrl} />}
              <p className="subscription-history__title">
                {dayjs(subscription.nextDeliveryDate).format('YYYY/MM/DD')}にお届け予定
              </p>
              <div className="subscription-history__productlist-wrapper">
              <div className={`subscription-history__productlist ${productCountClass}`}>
                <SubscriptionListProductThumbnails
                  subscribedItems={thumbItems}
                  fallbackLines={subscription.subscriptionLines}
                  productDisplayUnits={productDisplayUnits}
                />
              </div>
              </div>
              <Link
                to={`/subscriptions/${encodeURIComponent(subscription.subscriptionContractId)}`}
                onClick={() => {
                  postMessageGtag('click_go_to_detail_page', 'top');
                }}
              >
                <span className="btn detail-btn link-detail">お届け日・内容を変更する</span>
              </Link>
              <p className="subscription-history__changetitle">
                変更
                <span>
                  {dayjs(subscription.nextDeliveryDate)
                    .set('hour', 0)
                    .set('minute', 0)
                    .set('second', 0)
                    .set('millisecond', 0)
                    .subtract(6, 'day')
                    .format('MM/DD(dd)')}
                </span>
                まで
              </p>
              {!subscription.isSyncingSubscription && (
                <PayBackPointsRate
                  subscription={subscription}
                  setPointsRateModal={setPointsRateModal}
                />
              )}
            </>
          );
          // PAUSEDの契約
        } else {
          subscriptionContents = (
            <>
              <p className="subscription-history__pausedtitle">お届け停止中</p>
              <ReactiveSubscription
                subscriptionItem={subscription}
                setting={setting}
                productDisplayUnits={productDisplayUnits}
                subscribedItems={thumbItems}
              />
              {!subscription.isSyncingSubscription && (
                <PayBackPointsRate
                  subscription={subscription}
                  setPointsRateModal={setPointsRateModal}
                />
              )}
            </>
          );
        }

        return (
          <article
            className="subscription-history"
            key={index}
          >
            {subscriptionContents}
          </article>
        );
      });
    }
  }

  return (
    <div ref={root}>
      <div className="shell shell--small">
        <div className="account account__content">
          <section>
            {listContents}
            {subScriptionNp}
          </section>
        </div>
        <div className="account account__info">
          <div className="account__nav">
            <nav className="nav-account_1">
              <ul className="account_footer_menu"></ul>
            </nav>
          </div>
        </div>
      </div>
      <PayBackPointsRateModal
        active={pointsRateModal !== null}
        closeFunc={() => setPointsRateModal(null)}
        payBackPointsRateModal={pointsRateModal}
      />
    </div>
  );
};
